/**
 * AADHYAA Logo Component
 * 
 * Brand Integration Guidelines:
 * - Logo MUST NOT be altered, stretched, skewed, or recolored
 * - Minimum clear space: 16px around the logo
 * - Minimum size: 32px (for icon-only), 120px (for full logo)
 * - Maximum size: 512px (to prevent pixelation)
 * - Always maintain 1:1 aspect ratio
 * - No opacity changes, shadows, or effects
 */

import { memo } from 'react';

// Logo size presets following brand guidelines
const SIZES = {
  xs: { icon: 24, full: 80 },    // Minimum for micro contexts
  sm: { icon: 32, full: 120 },   // Small - header nav, badges
  md: { icon: 48, full: 160 },   // Medium - section headers
  lg: { icon: 64, full: 200 },   // Large - page headers
  xl: { icon: 96, full: 280 },   // XL - hero sections
  '2xl': { icon: 128, full: 360 }, // 2XL - splash screens
};

// Logo variants
const VARIANTS = {
  icon: '/logo-symbol.png',      // Icon only (wave pattern)
  full: '/aadhyaa-logo.png',     // Full logo with text
};

/**
 * Primary Logo Component
 * 
 * @param {string} variant - 'icon' | 'full' (default: 'full')
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' (default: 'md')
 * @param {string} className - Additional CSS classes
 * @param {boolean} responsive - Enable responsive scaling
 * @param {string} testId - data-testid for testing
 */
export const Logo = memo(({ 
  variant = 'full', 
  size = 'md', 
  className = '',
  responsive = false,
  testId = 'aadhyaa-logo'
}) => {
  const sizeConfig = SIZES[size] || SIZES.md;
  const dimension = variant === 'icon' ? sizeConfig.icon : sizeConfig.full;
  const src = VARIANTS[variant] || VARIANTS.full;
  
  return (
    <img 
      src={src}
      alt="AADHYAA"
      width={dimension}
      height={variant === 'icon' ? dimension : 'auto'}
      className={`
        object-contain
        select-none
        ${responsive ? 'w-full h-auto max-w-full' : ''}
        ${className}
      `.trim()}
      style={{
        minWidth: variant === 'icon' ? SIZES.xs.icon : SIZES.xs.full,
        maxWidth: SIZES['2xl'].full,
        aspectRatio: variant === 'icon' ? '1/1' : 'auto',
      }}
      draggable={false}
      data-testid={testId}
    />
  );
});

Logo.displayName = 'Logo';

/**
 * Logo Icon Only - For compact spaces
 */
export const LogoIcon = memo(({ size = 'md', className = '', testId = 'aadhyaa-logo-icon' }) => (
  <Logo variant="icon" size={size} className={className} testId={testId} />
));

LogoIcon.displayName = 'LogoIcon';

/**
 * Logo with Text - Horizontal lockup for headers
 */
export const LogoWithText = memo(({ 
  size = 'md', 
  className = '',
  showTagline = false,
  testId = 'aadhyaa-logo-text'
}) => {
  const sizeConfig = SIZES[size] || SIZES.md;
  
  return (
    <div 
      className={`flex items-center gap-3 ${className}`}
      data-testid={testId}
    >
      <Logo variant="full" size={size} />
      {showTagline && (
        <span className="text-xs text-[var(--text-muted)] font-light tracking-wide">
          Not predictions. Practice.
        </span>
      )}
    </div>
  );
});

LogoWithText.displayName = 'LogoWithText';

/**
 * Logo for Loading States - Subtle animation
 */
export const LogoLoading = memo(({ size = 'lg', className = '' }) => (
  <div className={`flex flex-col items-center justify-center ${className}`}>
    <div className="animate-pulse">
      <Logo variant="full" size={size} />
    </div>
    <div className="mt-4 flex gap-1">
      <span className="w-2 h-2 rounded-full bg-[var(--truffle)] animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 rounded-full bg-[var(--truffle)] animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 rounded-full bg-[var(--truffle)] animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
));

LogoLoading.displayName = 'LogoLoading';

/**
 * Logo for Reports - Minimal footer/header usage
 */
export const LogoReport = memo(({ position = 'header', className = '' }) => (
  <div 
    className={`
      flex items-center gap-2 
      ${position === 'footer' ? 'opacity-60' : 'opacity-80'}
      ${className}
    `}
  >
    <LogoIcon size="xs" />
    <span className="text-xs font-cinzel text-[var(--text-muted)] tracking-widest uppercase">
      Aadhyaa
    </span>
  </div>
));

LogoReport.displayName = 'LogoReport';

// Export size presets for external use
export const LOGO_SIZES = SIZES;

export default Logo;
